/**
 * 資金繰り(CF)画面(モックアップ準拠)。
 * 口座別残高 / 入出金一覧 / 手入力 / CSV取込(消込付き)/ 月末残高見込み。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '@core/components/Toast';
import { usePeriod } from '@core/components/PeriodProvider';
import { useMasters } from '@core/masters/MastersProvider';
import { formatCashAccountLabel } from '@core/lib/cashAccountLabel';
import { endOfMonth, toShortDate } from '@core/lib/date';
import { formatYen, formatYenSigned } from '@core/lib/money';
import { calcBalance, calcForecast, fetchCashAccounts, fetchCashEntries } from './cashRepo';
import ManualEntryForm from './ManualEntryForm';
import CsvImportPanel from './CsvImportPanel';
import TransferForm from './TransferForm';
import type { CashAccount, CashEntry } from '@core/lib/types';

export default function CashPage() {
  const toast = useToast();
  const { month } = usePeriod();
  const { companyName } = useMasters();
  const [accounts, setAccounts] = useState<CashAccount[]>([]);
  const [entriesByAccount, setEntriesByAccount] = useState<Record<string, CashEntry[]>>({});
  const [loading, setLoading] = useState(true);
  const [showManual, setShowManual] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [filterAccountId, setFilterAccountId] = useState<string>('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await fetchCashAccounts();
      setAccounts(list);
      const pairs = await Promise.all(
        list.map(async (a) => [a.id, await fetchCashEntries(a.id)] as const),
      );
      setEntriesByAccount(Object.fromEntries(pairs));
    } catch (e) {
      toast(e instanceof Error ? e.message : '資金繰りデータを取得できませんでした');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const monthEnd = endOfMonth(month);

  const balances = useMemo(() => {
    return accounts.map((a) => {
      const entries = entriesByAccount[a.id] ?? [];
      const balance = calcBalance(a, entries);
      return { account: a, balance, forecast: calcForecast(balance, entries, monthEnd) };
    });
  }, [accounts, entriesByAccount, monthEnd]);

  const totalBalance = balances.reduce((s, b) => s + b.balance, 0);
  const totalForecast = balances.reduce((s, b) => s + b.forecast, 0);

  const visibleEntries = useMemo(() => {
    const all = Object.entries(entriesByAccount).flatMap(([accountId, list]) =>
      list.map((e) => ({ ...e, cash_account_id: accountId })),
    );
    return all
      .filter((e) => (filterAccountId ? e.cash_account_id === filterAccountId : true))
      .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
      .slice(0, 200);
  }, [entriesByAccount, filterAccountId]);

  // 口座の表記は「会社名: 口座名」(2026-09-08 指示)
  const accountName = (id: string) => {
    const a = accounts.find((x) => x.id === id);
    return a ? formatCashAccountLabel(a, companyName(a.company_id)) : id;
  };

  if (loading) {
    return (
      <section className="screen">
        <div className="card">
          <p className="empty">読み込み中…</p>
        </div>
      </section>
    );
  }

  if (accounts.length === 0) {
    return (
      <section className="screen">
        <div className="card">
          <h3>口座が登録されていません</h3>
          <p className="hint">
            銀行口座・現金の登録が必要です。現時点ではマスタ管理画面に口座の編集機能がないため、
            登録が必要な場合は本体セッションへ依頼してください。
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="screen">
      <div className="cash-cards">
        <div className="cash total">
          <div className="nm">現預金合計</div>
          <div className="amt">{formatYenSigned(totalBalance)}</div>
          <div className="sub">
            {month.replace('-', '/')}月末見込み {formatYenSigned(totalForecast)}
          </div>
        </div>
        {balances.map(({ account, balance, forecast }) => (
          <div className="cash" key={account.id}>
            <div className="nm">{formatCashAccountLabel(account, companyName(account.company_id))}</div>
            <div className="amt">{formatYenSigned(balance)}</div>
            <div className="sub">月末見込み {formatYenSigned(forecast)}</div>
          </div>
        ))}
      </div>

      <div className="card">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          入出金
          <select
            style={{
              marginLeft: 'auto',
              padding: '6px 10px',
              border: '1px solid var(--line)',
              borderRadius: 6,
              fontFamily: 'inherit',
              fontSize: 13,
            }}
            value={filterAccountId}
            onChange={(e) => setFilterAccountId(e.target.value)}
          >
            <option value="">すべての口座</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
          <button className="btn-line" onClick={() => setShowImport(true)}>
            CSV取込
          </button>
          <button className="btn-line" onClick={() => setShowManual(true)}>
            手入力
          </button>
          <button
            className="btn-line"
            onClick={() => setShowTransfer(true)}
            disabled={accounts.length < 2}
            title={accounts.length < 2 ? '口座が2つ以上必要です' : undefined}
          >
            資金移動
          </button>
        </h3>

        {visibleEntries.length === 0 ? (
          <p className="empty">入出金の記録はまだありません。</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>日付</th>
                <th>口座</th>
                <th>内容</th>
                <th className="num">入金</th>
                <th className="num">出金</th>
                <th>状態</th>
              </tr>
            </thead>
            <tbody>
              {visibleEntries.map((e) => (
                <tr key={e.id}>
                  <td>{toShortDate(e.date)}</td>
                  <td>{accountName(e.cash_account_id)}</td>
                  <td>
                    {e.description ?? '—'}
                    {e.kind === 'transfer' ? (
                      <span className="hint"> (振替)</span>
                    ) : (
                      e.transaction_id && <span className="hint"> (経費)</span>
                    )}
                  </td>
                  <td className="num pos">{e.direction === 'in' ? formatYen(e.amount) : '—'}</td>
                  <td className="num neg">{e.direction === 'out' ? formatYen(e.amount) : '—'}</td>
                  <td>
                    <span className={`badge ${e.status === 'settled' ? 'b-ok' : 'b-plan'}`}>
                      {e.status === 'settled' ? '確定' : '予定'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <p className="hint" style={{ marginTop: 10 }}>
          経費を確定すると、取引日に「予定」の出金が自動で作られます。CSVを取り込むと、
          金額が一致し日付が±5営業日以内の予定と突合(消込)され、二重計上を防ぎます。
        </p>
      </div>

      {showManual && (
        <ManualEntryForm
          accounts={accounts}
          defaultAccountId={filterAccountId || (accounts[0]?.id ?? '')}
          onClose={() => setShowManual(false)}
          onSaved={() => void load()}
        />
      )}

      {showImport && (
        <CsvImportPanel
          accounts={accounts}
          defaultAccountId={filterAccountId || (accounts[0]?.id ?? '')}
          onClose={() => setShowImport(false)}
          onImported={() => void load()}
        />
      )}

      {showTransfer && (
        <TransferForm
          accounts={accounts}
          onClose={() => setShowTransfer(false)}
          onSaved={() => void load()}
        />
      )}
    </section>
  );
}
